import StatusIndicator, { StatusIndicatorProps } from '@amzn/awsui-components-core/status-indicator';
import React from 'react';
import { PnlReportConfig } from '../starGridTypes';
import { formatDurationMs, normalizeReportStatus, relativeTime, reportStatusLabel } from '../utils/reportStatus';
import styles from './styles/ReportHealth.module.scss';

export interface ReportHealthProps {
  readonly config: PnlReportConfig;
}

/** ISO / SQL-ish timestamp → "Sep 10, 2026, 01:16 UTC" style. */
function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
}

/** Byte size → "1.5 MB" / "384 KB" (mirrors xPT `formatBytes`). */
function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

/** Human step-name map — mirrors xPT STEP_LABELS but for the MV+StarRocks pipeline. */
const STEP_LABELS: Record<string, string> = {
  mv_initial_refresh: 'MV initial refresh',
  dim_values_compute: 'Dimension values',
  cube_stats: 'Cube stats',
  aurora_write: 'Metadata write'
};

const humaniseStep = (key: string): string =>
  STEP_LABELS[key] ??
  key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const STATUS_INDICATOR_TYPE: Record<string, StatusIndicatorProps.Type> = {
  READY: 'success',
  GENERATING: 'in-progress',
  FAILED: 'error',
  DELETED: 'stopped'
};

/**
 * Report Health & Stats block — rendered inside the Cloudscape HelpPanel (tool panel)
 * on the report detail view. Ported from xPT PnlReportHealth.tsx, adapted to
 * StarGrid's MV + StarRocks substrate: `report_generation_time_ms` = time the initial
 * MV refresh took, cube stats come from `information_schema.materialized_views`.
 */
const ReportHealth: React.FC<ReportHealthProps> = ({ config }) => {
  const status = normalizeReportStatus(config.status);
  const stepEntries = Object.entries(config.step_timings_ms ?? {}).filter(([, ms]) => typeof ms === 'number') as [string, number][];
  const maxStepMs = stepEntries.reduce((max, [, ms]) => (ms > max ? ms : max), 0);
  const totalStepsMs = stepEntries.reduce((sum, [, ms]) => sum + ms, 0);

  const cubeSuffix = [
    config.cube_size_bytes !== null && config.cube_size_bytes !== undefined ? formatBytes(config.cube_size_bytes) : null,
    config.cube_parts !== null && config.cube_parts !== undefined ? `${config.cube_parts} parts` : null
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div>
      {/* Compact metric strip — labelled values separated by dots */}
      <div className={styles.metricStrip}>
        <span className={styles.metric}>
          <span className={styles.metricLabel}>Status</span>
          <span className={styles.metricValue}>
            <StatusIndicator type={STATUS_INDICATOR_TYPE[status] ?? 'pending'}>{reportStatusLabel(status)}</StatusIndicator>
          </span>
        </span>
        <span className={styles.metricSep}>·</span>
        <span className={styles.metric}>
          <span className={styles.metricLabel}>Generated in</span>
          <span className={styles.metricValue}>{formatDurationMs(config.report_generation_time_ms ?? null)}</span>
        </span>
        <span className={styles.metricSep}>·</span>
        <span className={styles.metric}>
          <span className={styles.metricLabel}>Rows</span>
          <span className={styles.metricValue}>
            {config.row_count !== null && config.row_count !== undefined ? config.row_count.toLocaleString() : '—'}
          </span>
        </span>
        {cubeSuffix ? (
          <>
            <span className={styles.metricSep}>·</span>
            <span className={styles.metric}>
              <span className={styles.metricLabel}>Cube</span>
              <span className={styles.metricValue}>{cubeSuffix}</span>
            </span>
          </>
        ) : null}
        <span className={styles.metricSep}>·</span>
        <span className={styles.metric}>
          <span className={styles.metricLabel}>Measure</span>
          <span className={styles.metricValue}>{config.measure ?? '—'}</span>
        </span>
        {config.version ? (
          <>
            <span className={styles.metricSep}>·</span>
            <span className={styles.metric}>
              <span className={styles.metricLabel}>Version</span>
              <span className={styles.metricValue}>{config.version}</span>
            </span>
          </>
        ) : null}
      </div>

      {/* Audit — single subtle block */}
      <div className={styles.auditRow}>
        <span>
          Created by <strong>{config.mv_name ? 'system' : '—'}</strong> · {formatWhen(config.mv_last_refresh_finished_time)}
        </span>
        {config.refreshed_at ? (
          <span>
            Refreshed by <strong>{config.refreshed_by ?? '—'}</strong> · {formatWhen(config.refreshed_at)} · {relativeTime(config.refreshed_at)}
          </span>
        ) : null}
      </div>

      {/* Step timings — proportional bars so the dominant stage jumps out */}
      {stepEntries.length > 0 ? (
        <>
          <hr className={styles.divider} />
          <div className={styles.stepsHeader}>
            <span className={styles.stepsTitle}>Step Timings</span>
            <span className={styles.stepsSubtitle}>
              {stepEntries.length} steps · Σ {formatDurationMs(totalStepsMs)}
            </span>
          </div>
          <div className={styles.stepList}>
            {stepEntries.map(([step, ms]) => {
              const widthPct = maxStepMs > 0 ? Math.max(2, (ms / maxStepMs) * 100) : 0;
              const isHot = ms === maxStepMs;
              return (
                <React.Fragment key={step}>
                  <span className={styles.stepName}>{humaniseStep(step)}</span>
                  <div className={styles.stepBarTrack}>
                    <div className={`${styles.stepBarFill} ${isHot ? styles.stepBarFillHot : ''}`} style={{ width: `${widthPct}%` }} />
                  </div>
                  <span className={styles.stepDuration}>{formatDurationMs(ms)}</span>
                </React.Fragment>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ReportHealth;
